/**
 * One-click "Report to Motzu" button for sync run warnings and failures.
 *
 * The product page asks the user to describe the problem; here the pipeline
 * already wrote the message, so the click submits it verbatim with no dialog —
 * run context travels in its own fields, never appended to the description.
 * Repeat clicks collapse server-side on sourceKey, so the same problem is not
 * filed again on every run.
 */

import React from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { CheckCircleOutline, FlagOutlined } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { apiService } from '../services/api';
import {
  SyncIssueReportTarget,
  syncIssueCategory,
  syncIssueSourceKey,
} from './syncIssueReport';

interface ReportSyncIssueButtonProps {
  issue: SyncIssueReportTarget;
  /** True once this sourceKey has a report open or in review. */
  reported?: boolean;
  onReported: (sourceKey: string, duplicate: boolean) => void;
  onError: (message: string) => void;
}

const ReportSyncIssueButton: React.FC<ReportSyncIssueButtonProps> = ({
  issue,
  reported = false,
  onReported,
  onError,
}) => {
  const sourceKey = syncIssueSourceKey(issue);
  // Nothing to report when the pipeline wrote no message: the description is
  // the message, and the API rejects an empty one.
  const reportable = Boolean(issue.productId && issue.message.trim());

  const reportMutation = useMutation({
    mutationFn: () =>
      apiService.submitProductIssue({
        productId: issue.productId,
        productSku: issue.productSku || '',
        productName: issue.productName || '',
        productBrand: issue.productBrand || '',
        category: syncIssueCategory(issue.code || issue.fallbackCode),
        description: issue.message,
        source: 'sync_run',
        sourceKey,
        issueCode: issue.code || issue.fallbackCode,
        syncRunId: issue.syncRunId,
        integrationId: issue.integrationId,
        integrationName: issue.integrationName,
      }),
    onSuccess: (result) => onReported(sourceKey, Boolean(result?.duplicate)),
    onError: (error: any) => {
      // The API's error handler responds { error: { message, statusCode, … } },
      // so read the nested message before falling back to flatter shapes.
      const payload = error?.response?.data;
      const apiMessage =
        payload?.error?.message ||
        (typeof payload?.error === 'string' ? payload.error : undefined) ||
        payload?.message ||
        error?.message;
      onError(
        apiMessage ? `Failed to report to Motzu: ${apiMessage}` : 'Failed to report to Motzu. Please try again.',
      );
    },
  });

  if (reported) {
    return (
      <Button
        size="small"
        variant="text"
        disabled
        startIcon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
        sx={{ minWidth: 0, px: 0.5, fontSize: '0.75rem' }}
      >
        Reported to Motzu
      </Button>
    );
  }

  return (
    <Tooltip title="Send this message to the Motzu team as a product issue">
      <span>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          disabled={!reportable || reportMutation.isPending}
          onClick={() => reportMutation.mutate()}
          startIcon={
            reportMutation.isPending ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <FlagOutlined sx={{ fontSize: 14 }} />
            )
          }
          sx={{ minWidth: 0, px: 1, fontSize: '0.75rem' }}
        >
          {reportMutation.isPending ? 'Reporting…' : 'Report to Motzu'}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ReportSyncIssueButton;
